import p2 from 'p2';
import { Sprite } from 'pixi.js';

import { Shared } from '../../App';
import { CullingComponent } from '../components/CullingComponent';
import { Game } from '../Game';
import { PhysicsCollisionGroup, PhysicsShapeType, PhysicsUtils } from '../physics';
import { PhysicsComponent } from '../physics/PhysicsComponent';
import { ChunkSpriteData } from '../systems/ChunkSystem';
import { BaseEntity } from './BaseEntity';

export interface FlagEndEntityParams
{
    type: string,
    x: number,
    y: number,
    w: number,
    h: number,
    sx: number,
    sy: number,
    r: number,
    img: string,
}

export class FlagEndEntityBack extends BaseEntity
{
    public sprite: Sprite;
    public params!: FlagEndEntityParams;
    public physics: PhysicsComponent;
    public culling: CullingComponent;

    public reached = false;
    public bodyHeight = 1024;
    public flagHeight = 235;

    constructor()
    {
        super();
        this.physics = this.addComponent(PhysicsComponent);
        this.culling = this.addComponent(CullingComponent);
        this.physics.culling = true;
        this.sprite = Sprite.from(Shared.assets.getTexture('end-back'));
        this.sprite.anchor.set(0.5);
        this.view.addChild(this.sprite);
        this.sprite.x = -30;
        this.sprite.y = (this.bodyHeight / 2) - (this.flagHeight / 2) - 40;
    }

    public build(params: FlagEndEntityParams): void
    {
        this.physics.clearParts();
        this.params = params;

        this.sprite.scale.x = this.params.sx;
        this.sprite.scale.y = this.params.sy;
        this.sprite.width = this.params.w;
        this.sprite.height = this.params.h;
        this.z = -0.3;

        const body = PhysicsUtils.createBody({
            x: this.params.x,
            y: this.params.y - (this.bodyHeight / 2) + (this.flagHeight / 2),
            angle: this.params.r,
            mass: 0,
        }, {
            type: PhysicsShapeType.BOX,
            width: this.params.w * 0.5,
            height: this.bodyHeight,
            group: PhysicsCollisionGroup.OBJECT,
            mask: PhysicsCollisionGroup.VEHICLE,
            sensor: true,
        });

        this.physics.addParts(body);
        this.culling.updateBounds();
    }

    public levelReset(): void
    {
        this.reached = false;
    }

    protected onBeginContact(other: p2.Body): void
    {
        if (this.reached) return;

        const game = this.scene as Game;

        if (game.race.running && game.vehicle.assembled && other.physicsComponent?.entity === game.vehicle.character)
        {
            this.reached = true;
            game.vehicle.finishRace();
        }
    }
}

export class FlagEndEntityFront extends BaseEntity
{
    public sprite!: Sprite;
    public params!: FlagEndEntityParams;
    public culling: CullingComponent;

    constructor()
    {
        super();
        this.culling = this.addComponent(CullingComponent);
    }

    public build(params: FlagEndEntityParams): void
    {
        this.params = params;
        this.sprite = Sprite.from(Shared.assets.getTexture('end-front'));
        this.view.addChild(this.sprite);
        this.sprite.anchor.set(0.5);
        this.sprite.scale.x = this.params.sx;
        this.sprite.scale.y = this.params.sy;
        this.sprite.width = this.params.w;
        this.sprite.height = this.params.h;
        this.z = 0.1;
        this.rotation = this.params.r;
        this.x = this.params.x;
        this.y = this.params.y + 10;
        this.culling.updateBounds();
    }
}

export class FlagEndFactory
{
    public static match(data: ChunkSpriteData): boolean
    {
        return data.img !== undefined && data.img.endsWith('end-flag');
    }

    public static create(game: Game, params: FlagEndEntityParams): void
    {
        const back = game.pool.get(FlagEndEntityBack) as any;

        back.build(params);

        game.level.addEntity(back);

        const front = game.pool.get(FlagEndEntityFront) as any;

        front.build(params);

        game.level.addEntity(front);

        game.level.courseEndX = params.x;
        game.level.courseEndY = params.y;
    }
}
